<template>
  <div class="chat-image-selection">
    <div
      v-if="title || effectiveDescription"
      class="chat-image-selection__header mb-3"
    >
      <p
        v-if="displayTitle"
        class="text-body-2 font-weight-medium mb-1"
      >
        {{ displayTitle }}
      </p>
      <p
        v-if="effectiveDescription"
        class="text-caption text-medium-emphasis mb-0"
      >
        {{ effectiveDescription }}
      </p>
    </div>
    <div class="chat-image-selection__grid">
      <button
        v-for="(candidate, index) in visibleCandidates"
        :key="`${candidate.url}-${index}`"
        type="button"
        class="chat-image-selection__tile"
        :class="{ 'chat-image-selection__tile--selected': selectedUrl === candidate.url }"
        :disabled="busy"
        @click="selectUrl(candidate.url)"
      >
        <r-img
          :src="candidate.url"
          cover
          class="chat-image-selection__img"
          @error="onImageError(candidate.url)"
        />
      </button>
      <button
        v-if="allowUpload"
        type="button"
        class="chat-image-selection__tile chat-image-selection__tile--upload"
        :disabled="busy || !companyId"
        @click="triggerUpload"
      >
        <v-icon
          size="28"
          color="primary"
        >
          mdi-plus
        </v-icon>
        <span class="text-caption font-weight-medium mt-1">
          {{ t('onboarding.wizard.chat-widgets.upload-image') }}
        </span>
      </button>
    </div>
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      class="d-none"
      @change="onFileSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface ImageCandidate {
  url: string
  confidence?: number
  alt_text?: string
}

const props = defineProps<{
  candidates: ImageCandidate[]
  formKey: string
  allowUpload?: boolean
  companyId?: string | null
  busy?: boolean
  title?: string
  description?: string
}>()

const emit = defineEmits<{
  select: [payload: { formKey: string; url: string }]
  upload: [payload: { formKey: string; file: File }]
}>()

const { t } = useI18n()
const fileInputRef = ref<HTMLInputElement | null>(null)
const selectedUrl = ref<string | null>(null)

// A scraped candidate URL can be broken (404, blocked, not an image). We hide any
// tile whose image fails to load, so the user never sees an empty grey box — and
// the copy adapts when nothing displayable remains.
const failedUrls = ref<Set<string>>(new Set())

const onImageError = (url: string) => {
  failedUrls.value = new Set(failedUrls.value).add(url)
}

const visibleCandidates = computed(() =>
  props.candidates.filter((c) => c.url && c.url.trim() && !failedUrls.value.has(c.url))
)

// Backend sends the title lowercased (e.g. "company logo"). Capitalize the first
// letter for the header — the mid-sentence {label} in the description stays lowercase.
const displayTitle = computed((): string => {
  const raw = props.title?.trim()
  if (!raw) return ''
  return raw.charAt(0).toUpperCase() + raw.slice(1)
})

const effectiveDescription = computed((): string => {
  if (visibleCandidates.value.length > 0) return props.description ?? ''
  // No usable images to pick from — never claim images were found.
  return t('onboarding.wizard.chat-widgets.image-none-found', {
    label: props.title || t('onboarding.wizard.chat-widgets.image-fallback-label'),
  })
})

const selectUrl = (url: string) => {
  selectedUrl.value = url
  emit('select', { formKey: props.formKey, url })
}

const triggerUpload = () => {
  fileInputRef.value?.click()
}

const onFileSelected = (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  emit('upload', { formKey: props.formKey, file })
}
</script>

<style scoped>
.chat-image-selection__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.chat-image-selection__tile {
  aspect-ratio: 1;
  border: 2px solid transparent;
  border-radius: 12px;
  overflow: hidden;
  padding: 0;
  background: rgba(var(--v-theme-on-surface), 0.04);
  cursor: pointer;
}

.chat-image-selection__tile--selected {
  border-color: rgb(var(--v-theme-primary));
}

.chat-image-selection__tile--upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(var(--v-theme-primary), 0.45);
  background: rgba(var(--v-theme-primary), 0.06);
}

.chat-image-selection__img {
  width: 100%;
  height: 100%;
}
</style>
